<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher<{ win: number }>();
  const dispatchLoss = createEventDispatcher<void>();
  
  let { onBack, playerName = 'Player' } = $props();
  
  let playerPosition = $state(0);
  let aiPosition = $state(0);
  let currentRoll = $state(0);
  let gameMessage = $state('Roll the dice to start!');
  let isPlayerTurn = $state(true);
  let difficulty = $state(1);
  let snakes = $state([16, 47, 49, 56, 62, 64, 87, 93, 95, 99]);
  let ladders = $state([1, 4, 9, 21, 28, 36, 51, 71, 80]);
  
  function rollDice() {
    if (!isPlayerTurn) return;
    
    currentRoll = Math.floor(Math.random() * 6) + 1;
    gameMessage = `You rolled a ${currentRoll}!`;
    
    setTimeout(() => movePlayer(), 500);
  }
  
  function movePlayer() {
    playerPosition += currentRoll;
    
    // Check for snakes
    if (snakes.includes(playerPosition)) {
      gameMessage = 'Oh no! A snake! 🐍';
      playerPosition = Math.max(0, playerPosition - 10);
    }
    
    // Check for ladders
    if (ladders.includes(playerPosition)) {
      gameMessage = 'Yay! A ladder! 🪜';
      playerPosition += 5;
    }
    
    // Check win
    if (playerPosition >= 100) {
      handleWin();
      return;
    }
    
    isPlayerTurn = false;
    setTimeout(() => aiTurn(), 1000);
  }
  
  function aiTurn() {
    const aiRoll = Math.floor(Math.random() * 6) + 1;
    gameMessage = `AI rolled ${aiRoll}`;
    
    setTimeout(() => {
      aiPosition += aiRoll + difficulty; // AI gets bonus based on difficulty
      
      if (aiPosition >= 100) {
        handleLoss();
        return;
      }
      
      isPlayerTurn = true;
      gameMessage = 'Your turn! Roll the dice.';
    }, 800);
  }
  
  function handleWin() {
    const points = 150 * difficulty;
    gameMessage = `🎉 You won! +${points} points!`;
    dispatch('win', points);
    difficulty = Math.min(difficulty + 1, 5);
  }
  
  function handleLoss() {
    gameMessage = 'AI reached 100 first! Game Over.';
    dispatchLoss();
  }
  
  function resetGame() {
    playerPosition = 0;
    aiPosition = 0;
    currentRoll = 0;
    gameMessage = 'New game! Roll to start.';
    isPlayerTurn = true;
  }
</script>

<div class="ludo-game">
  <div class="game-header">
    <button class="back-btn" onclick={onBack}>← Back</button>
    <h2>🎲 Ludo / Snakes & Ladders</h2>
    <div class="player-info">{playerName} vs AI</div>
  </div>
  
  <div class="game-message">{gameMessage}</div>
  
  <div class="game-board">
    <div class="track">
      {#each Array(100) as _, i}
        <div 
          class="cell {i === playerPosition ? 'player' : ''} {i === aiPosition ? 'ai' : ''}"
        >
          {i + 1}
        </div>
      {/each}
    </div>
    
    <div class="positions">
      <div class="position-indicator player-pos">
        <span>👤 You: {playerPosition}</span>
      </div>
      <div class="position-indicator ai-pos">
        <span>🤖 AI: {aiPosition}</span>
      </div>
    </div>
  </div>
  
  <div class="dice-section">
    <div class="dice-display">{currentRoll > 0 ? '🎲 ' + currentRoll : '🎲'}</div>
    <button 
      class="roll-btn" 
      onclick={rollDice}
      disabled={!isPlayerTurn}
      class:disabled={!isPlayerTurn}
    >
      {isPlayerTurn ? 'Roll Dice' : 'AI Thinking...'}
    </button>
  </div>
  
  <div class="game-controls">
    <button class="action-btn" onclick={resetGame}>🔄 New Game</button>
  </div>
  
  <div class="difficulty-indicator">
    <span>Difficulty:</span>
    <div class="level-dots">
      {#each Array(5) as _, i}
        <span class="dot {i < difficulty ? 'active' : ''}"></span>
      {/each}
    </div>
  </div>
</div>

<style>
  .ludo-game {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .game-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }
  
  .back-btn, .action-btn {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
  }
  
  h2 {
    color: #2d5016;
    font-size: 1.5rem;
  }
  
  .player-info {
    color: #5a7a5a;
    font-weight: 600;
  }
  
  .game-message {
    text-align: center;
    padding: 15px;
    background: linear-gradient(135deg, #f0fff4, #d4edda);
    border-radius: 15px;
    margin-bottom: 20px;
    font-weight: 600;
    color: #2d5016;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .game-board {
    background: white;
    border-radius: 15px;
    padding: 20px;
    box-shadow: 0 5px 20px rgba(46, 204, 113, 0.15);
  }
  
  .track {
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: 3px;
    margin-bottom: 20px;
  }
  
  .cell {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0fff4;
    border-radius: 5px;
    font-size: 0.7rem;
    color: #2d5016;
    position: relative;
  }
  
  .cell.player {
    background: #2ecc71;
    color: white;
    font-weight: bold;
    box-shadow: 0 0 10px #2ecc71;
  }
  
  .cell.ai {
    background: #e74c3c;
    color: white;
    font-weight: bold;
    box-shadow: 0 0 10px #e74c3c;
  }
  
  .cell.player.ai {
    background: linear-gradient(135deg, #2ecc71, #e74c3c);
  }
  
  .positions {
    display: flex;
    justify-content: space-around;
    margin-bottom: 15px;
  }
  
  .position-indicator {
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: 600;
  }
  
  .player-pos {
    background: #2ecc71;
    color: white;
  }
  
  .ai-pos {
    background: #e74c3c;
    color: white;
  }
  
  .dice-section {
    text-align: center;
    margin: 20px 0;
  }
  
  .dice-display {
    font-size: 3rem;
    margin-bottom: 15px;
  }
  
  .roll-btn {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border: none;
    padding: 15px 40px;
    font-size: 1.2rem;
    border-radius: 50px;
    cursor: pointer;
    font-weight: 700;
    transition: all 0.3s ease;
    box-shadow: 0 5px 20px rgba(46, 204, 113, 0.4);
  }
  
  .roll-btn:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(46, 204, 113, 0.6);
  }
  
  .roll-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .game-controls {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  
  .difficulty-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
    color: #2d5016;
  }
  
  .level-dots {
    display: flex;
    gap: 5px;
  }
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e0e0e0;
  }
  
  .dot.active {
    background: #2ecc71;
    box-shadow: 0 0 10px #2ecc71;
  }
  
  @media (max-width: 500px) {
    .track {
      grid-template-columns: repeat(5, 1fr);
    }
    
    .cell {
      font-size: 0.6rem;
    }
  }
</style>
